// src/pages/order-tracking/order-tracking.component.jsx
import React, { useState } from 'react';
import { firestore, collection, getDocs, query, where } from '../../firebase/firebase.utils';
import './order-tracking.styles.scss';

const OrderTracking = () => {
  const [searchType, setSearchType] = useState('phone'); // 'phone' or 'order'
  const [searchValue, setSearchValue] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchValue.trim()) {
      setError('Please enter a phone number or order number');
      return;
    }

    setLoading(true);
    setError('');
    setSearched(true);

    try {
      let q;
      const ordersRef = collection(firestore, 'orders');

      if (searchType === 'phone') {
        q = query(ordersRef, where('phone', '==', searchValue.trim()));
      } else {
        q = query(ordersRef, where('orderNumber', '==', searchValue.trim().toUpperCase()));
      }

      const querySnapshot = await getDocs(q);
      
      const foundOrders = [];
      querySnapshot.forEach((doc) => {
        foundOrders.push({ id: doc.id, ...doc.data() });
      });

      setOrders(foundOrders);
      setLoading(false);
    } catch (error) {
      console.error('Error searching orders:', error);
      setError('Error searching orders. Please try again.');
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending': return 'badge-pending';
      case 'processing': return 'badge-processing';
      case 'shipped': return 'badge-shipped';
      case 'delivered': return 'badge-delivered';
      case 'cancelled': return 'badge-cancelled';
      default: return 'badge-pending';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'pending': return '🟡 Pending';
      case 'processing': return '🔵 Processing';
      case 'shipped': return '🟣 Shipped';
      case 'delivered': return '🟢 Delivered';
      case 'cancelled': return '🔴 Cancelled';
      default: return status || 'Pending';
    }
  };

  return (
    <div className="order-tracking-page">
      <div className="tracking-container">
        <div className="tracking-header">
          <h1>📦 Track Your Order</h1>
          <p>Enter your phone number or order number to track your order status</p>
        </div>

        <form onSubmit={handleSearch} className="search-form">
          <div className="search-type-toggle">
            <button
              type="button"
              className={`toggle-btn ${searchType === 'phone' ? 'active' : ''}`}
              onClick={() => setSearchType('phone')}
            >
              📱 Search by Phone
            </button>
            <button
              type="button"
              className={`toggle-btn ${searchType === 'order' ? 'active' : ''}`}
              onClick={() => setSearchType('order')}
            >
              🔢 Search by Order #
            </button>
          </div>

          <div className="search-input-group">
            <input
              type="text"
              placeholder={searchType === 'phone' ? 'Enter your phone number (e.g., 03001234567)' : 'Enter your order number (e.g., ORD-12345678)'}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              required
            />
            <button type="submit" disabled={loading}>
              {loading ? 'Searching...' : '🔍 Track Order'}
            </button>
          </div>

          {error && <p className="error-message">{error}</p>}
        </form>

        {searched && !loading && (
          <div className="results-section">
            {orders.length === 0 ? (
              <div className="no-orders">
                <span>🔍</span>
                <h3>No Orders Found</h3>
                <p>We couldn't find any orders with the provided information.</p>
                <p className="help-text">Please check your phone number or order number and try again.</p>
              </div>
            ) : (
              <div className="orders-list">
                <h2>Your Orders ({orders.length})</h2>
                {orders.map((order) => (
                  <div className="order-card" key={order.id}>
                    <div className="order-card-header">
                      <div className="order-number">
                        <span className="label">Order #</span>
                        <span className="value">{order.orderNumber || 'N/A'}</span>
                      </div>
                      <div className="order-status">
                        <span className={`status-badge ${getStatusBadge(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </span>
                      </div>
                    </div>

                    <div className="order-card-body">
                      <div className="order-details-grid">
                        <div className="detail-item">
                          <span className="label">Date</span>
                          <span className="value">{formatDate(order.createdAt)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Items</span>
                          <span className="value">{order.itemCount || 0}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Total</span>
                          <span className="value">Rs. {order.totalAmount?.toLocaleString() || 0}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Delivery</span>
                          <span className="value">{order.city || 'N/A'}</span>
                        </div>
                      </div>

                      <div className="order-items">
                        <h4>Order Items</h4>
                        <ul>
                          {order.items?.map((item, index) => (
                            <li key={index}>
                              <span>{item.name}</span>
                              <span>x{item.quantity}</span>
                              <span>Rs. {(item.price * item.quantity).toLocaleString()}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="order-address">
                        <h4>Delivery Address</h4>
                        <p>{order.address}</p>
                        <p>{order.city} {order.postalCode && `- ${order.postalCode}`}</p>
                      </div>

                      <div className="order-timeline">
                        <h4>Order Timeline</h4>
                        <div className="timeline">
                          <div className={`timeline-item ${order.status === 'pending' || order.status === 'processing' || order.status === 'shipped' || order.status === 'delivered' ? 'completed' : ''}`}>
                            <span className="dot"></span>
                            <span className="timeline-label">Order Placed</span>
                            <span className="timeline-date">{formatDate(order.createdAt)}</span>
                          </div>
                          <div className={`timeline-item ${order.status === 'processing' || order.status === 'shipped' || order.status === 'delivered' ? 'completed' : ''}`}>
                            <span className="dot"></span>
                            <span className="timeline-label">Processing</span>
                          </div>
                          <div className={`timeline-item ${order.status === 'shipped' || order.status === 'delivered' ? 'completed' : ''}`}>
                            <span className="dot"></span>
                            <span className="timeline-label">Shipped</span>
                          </div>
                          <div className={`timeline-item ${order.status === 'delivered' ? 'completed' : ''}`}>
                            <span className="dot"></span>
                            <span className="timeline-label">Delivered</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;